import React from 'react';
import { 
  XMarkIcon, 
  UserIcon
} from '@heroicons/react/24/outline';

interface ActiveUser {
  _id: string;
  name: string;
  email: string;
}

interface StudiedCard {
  _id: string;
  front: string;
  back: string;
  deck: string;
  correct: boolean;
  timeSpent: number;
  quality: number;
  userId?: string;
  userName?: string;
  userEmail?: string;
}

interface StudySession {
  _id: string;
  user: {
    _id: string;
    name: string;
    email: string;
  };
  deck: {
    _id: string;
    name: string;
  };
  cardsStudied: number;
  correctAnswers: number;
  duration: number;
  completedAt: string;
  accuracy: number;
}

interface QuizSession {
  _id: string;
  user: {
    _id: string;
    name: string;
    email: string;
  };
  score?: number;
  totalQuestions?: number;
  correctAnswers?: number;
  timeSpent?: number;
  startedAt?: string;
  completedAt?: string;
}

interface MatchSession {
  _id: string;
  user: {
    _id: string;
    name: string;
    email: string;
  };
  timeSpent?: number;
  movesCount?: number;
  startedAt?: string;
  completedAt?: string;
}

interface DayStatsData {
  date: string;
  activeUsers: ActiveUser[];
  cardsStudied: StudiedCard[];
  sessions?: StudySession[];
  flashcardSessions?: StudySession[];
  quizSessions?: QuizSession[];
  matchSessions?: MatchSession[];
  totalSessions: number;
  totalStudyTime: number;
  totalCardsStudied: number;
  totalCorrectAnswers: number;
  flashcardsCount?: number;
  quizzesCount?: number;
  matchesCount?: number;
  studiesCount?: number;
}

interface DayStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: DayStatsData | null;
  loading: boolean;
}

const formatTime = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds}s`;
  } else if (seconds < 3600) {
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  } else {
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  }
};

const formatDate = (dateString: string): string => {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const DayStatsModal: React.FC<DayStatsModalProps> = ({ isOpen, onClose, data, loading }) => {
  const [selectedUserId, setSelectedUserId] = React.useState<string | null>(null);

  if (!isOpen) return null;

  const selectedUser = selectedUserId
    ? data?.activeUsers?.find(user => user._id?.toString() === selectedUserId)
    : null;

  const flashcardSessions = selectedUserId
    ? (data?.flashcardSessions ?? data?.sessions ?? []).filter(session => session.user._id?.toString() === selectedUserId)
    : (data?.flashcardSessions ?? data?.sessions ?? []);

  const quizSessions = selectedUserId
    ? (data?.quizSessions ?? []).filter(session => session.user._id?.toString() === selectedUserId)
    : (data?.quizSessions ?? []);

  const matchSessions = selectedUserId
    ? (data?.matchSessions ?? []).filter(session => session.user._id?.toString() === selectedUserId)
    : (data?.matchSessions ?? []);

  const renderSessionGroup = (
    title: string,
    items: any[],
    emptyText: string,
    accentClass: string,
    renderItem: (item: any) => React.ReactNode,
  ) => (
    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center justify-between">
        <span className={`flex items-center ${accentClass}`}>
          {title} <span className="ml-2 text-sm font-medium text-gray-500 dark:text-gray-400">({items.length})</span>
        </span>
      </h4>

      <div className="space-y-2 max-h-72 overflow-y-auto">
        {items.length > 0 ? (
          items.map((item) => (
            <div key={item._id} className="bg-white dark:bg-gray-800 rounded-md p-3 border border-gray-200 dark:border-gray-600">
              {renderItem(item)}
            </div>
          ))
        ) : (
          <p className="text-gray-500 dark:text-gray-400 text-center py-4">{emptyText}</p>
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                {data ? `Statistics for ${formatDate(data.date)}` : 'Loading...'}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-400"></div>
                <span className="ml-2 text-gray-700 dark:text-gray-300">Loading statistics...</span>
              </div>
            ) : data ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                  {selectedUser && (
                    <div className="xl:col-span-3 mb-2 p-2 bg-blue-50 dark:bg-blue-900 rounded-md">
                      <p className="text-sm text-blue-700 dark:text-blue-200">
                        Showing activity for: <strong>{selectedUser.name}</strong>
                      </p>
                    </div>
                  )}

                  {renderSessionGroup(
                    'Flashcard Sessions',
                    flashcardSessions,
                    'No flashcard sessions',
                    'text-sky-600 dark:text-sky-400',
                    (session) => (
                      <>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 mr-2">
                            <p className="font-medium text-gray-900 dark:text-white text-sm">{session.user?.name || 'Unknown User'}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{session.deck?.name || 'Quick Study'}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-600 dark:text-gray-300">{formatTime(session.duration)}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(session.completedAt).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs px-2 py-1 rounded-full bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-200">
                            {session.cardsStudied} cards
                          </span>
                          <span className="text-xs px-2 py-1 rounded-full bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-200">
                            {session.accuracy}% accuracy
                          </span>
                        </div>
                      </>
                    )
                  )}

                  {renderSessionGroup(
                    'Quiz Sessions',
                    quizSessions,
                    'No quiz sessions',
                    'text-violet-600 dark:text-violet-400',
                    (session) => (
                      <>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 mr-2">
                            <p className="font-medium text-gray-900 dark:text-white text-sm">{session.user?.name || 'Unknown User'}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {session.totalQuestions ? `${session.correctAnswers ?? 0}/${session.totalQuestions} correct` : 'Quiz attempt'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-600 dark:text-gray-300">{session.timeSpent ? formatTime(session.timeSpent) : '—'}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {session.completedAt ? new Date(session.completedAt).toLocaleTimeString() : '—'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs px-2 py-1 rounded-full bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-200">
                            {session.score !== undefined ? `${session.score}%` : `${session.correctAnswers ?? 0} correct`}
                          </span>
                          <span className="text-xs px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200">
                            {session.totalQuestions ? `${session.totalQuestions} questions` : 'Quiz'}
                          </span>
                        </div>
                      </>
                    )
                  )}

                  {renderSessionGroup(
                    'Match Sessions',
                    matchSessions,
                    'No match sessions',
                    'text-orange-600 dark:text-orange-400',
                    (session) => (
                      <>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 mr-2">
                            <p className="font-medium text-gray-900 dark:text-white text-sm">{session.user?.name || 'Unknown User'}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{session.movesCount ? `${session.movesCount} moves` : 'Match attempt'}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-600 dark:text-gray-300">{session.timeSpent ? formatTime(session.timeSpent) : '—'}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {session.completedAt ? new Date(session.completedAt).toLocaleTimeString() : '—'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs px-2 py-1 rounded-full bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-200">
                            {session.movesCount ?? 0} moves
                          </span>
                          <span className="text-xs px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-800 text-amber-700 dark:text-amber-200">
                            Match
                          </span>
                        </div>
                      </>
                    )
                  )}
                </div>

                <div className="flex justify-end">
                  {selectedUser && (
                    <button
                      onClick={() => setSelectedUserId(null)}
                      className="text-sm px-3 py-2 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200 rounded-md hover:bg-blue-200 dark:hover:bg-blue-700"
                    >
                      Show all users
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">No data available for this day</p>
              </div>
            )}
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DayStatsModal;